import { CommonModule } from "@angular/common";
import { Component, inject, Input } from "@angular/core";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { testPrioritizationModalConfig } from "../../../core/generic-templates/modal-config-templates";
import { INZorroOutput } from "../../../core/models/common";
import { ZorroTableComponent } from "../../common/custom-table/zorro-table.component";
import { LoadingComponent } from "../../common/loading/loading.component";
import { NoDataComponent } from "../../common/no-data/no-data.component";
import { GenericModalComponent } from "../user-story-basic-view/generic-modal.component";

/**
 * A modal component used to display a table view and manage actions related to table data.
 * The component allows users to view data in a table format, open detailed modals for specific data,
 * and handle actions triggered by the table.
 */
@Component({
  selector: "app-table-view-modal",
  standalone: true,
  imports: [
    CommonModule,
    ZorroTableComponent,
    NoDataComponent,
    LoadingComponent,
  ],
  templateUrl: "./table-view-modal.component.html",
  styleUrl: "./table-view-modal.component.scss",
})
export class TableViewModalComponent {
  activeModal = inject(NgbActiveModal);
  private modalService = inject(NgbModal);
  isActive: boolean = false;

  @Input()
  tableData: any = {};

  @Input()
  title: string = "";

  @Input()
  loader: any;

  ngOnInit() {
   
    // Add id field to each row if not already present
    if (this.tableData?.rows) {
      this.tableData.rows = this.tableData.rows.map((row: any, index: number) => ({
        ...row,
        id: row.id ?? index + 1
      }));
    }

    // Configure severity columns with classMapping
    if (this.tableData?.columns) {
      this.tableData.columns = this.tableData.columns.map((column: any) => {
        // Check if this is a severity or priority column (case insensitive)
        const columnName = column.display_name?.toLowerCase() || '';
        const columnKey = column.key?.toLowerCase() || '';
        
        if (columnKey.includes('severity') || columnKey.includes('priority') || 
            columnName.includes('severity') || columnName.includes('priority')) {
          const updatedColumn = {
            ...column,
            _config: {
              type: 'severity-badge',
              classMapping: {
                critical: 'modal-severity-critical',
                high: 'modal-severity-high',
                medium: 'modal-severity-medium',
                low: 'modal-severity-low',
                major: 'modal-severity-major',
                minor: 'modal-severity-minor',
                Critical: 'modal-severity-critical',
                High: 'modal-severity-high',
                Medium: 'modal-severity-medium',
                Low: 'modal-severity-low',
                Major: 'modal-severity-major',
                Minor: 'modal-severity-minor',
                CRITICAL: 'modal-severity-critical',
                HIGH: 'modal-severity-high',
                MEDIUM: 'modal-severity-medium',
                LOW: 'modal-severity-low',
                MAJOR: 'modal-severity-major',
                MINOR: 'modal-severity-minor'
              }
            }
          };
          return updatedColumn;
        }
        return column;
      });
    }
  }

  /**
   * Opens a detailed modal with additional information about a particular data row.
   * This method is invoked when a user clicks on a row to view its details in a modal.
   *
   * @param data The data to be passed to the detailed modal.
   */
  openDetailedModal(data: any) {
    const modalRef = this.modalService.open(GenericModalComponent, {
      centered: true,
      size: "xl",
      backdrop: 'static',      
    });
    modalRef.componentInstance.data = data;
    modalRef.componentInstance.contentList =
      testPrioritizationModalConfig.testCaseSourceCodeMapping;
    modalRef.componentInstance.title =
      "Test Case to Source Code Mapping - Details";
  }

  /**
   * Handles actions triggered by user interactions with the table.
   * Specifically handles the "open_modal" action, which opens a detailed modal
   * with more information about the selected row.
   *
   * @param event The event triggered by the table action, containing the row data and action details.
   */
  handleTableActions(event: INZorroOutput) {
    if (event.data.column?.key === "open_modal") {
      this.openDetailedModal(event.data.row);
    }
  }

  /**
   * Returns the appropriate CSS class for a severity/priority lozenge based on its value.
   * @param value The severity level (critical, high, medium, low, major, minor)
   * @returns The corresponding CSS class name
   */
  getLozengeClass(value: any) {
    switch (value) {
      case "critical":
        return "critical-priority";
      case "high":
        return "high-priority";
      case "medium":
        return "medium-priority";
      case "low":
        return "low-priority";
      case "major":
        return "major-priority";
      case "minor":
        return "minor-priority";
      default:
        return "default-priority";
    }
  }
}
